import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import { Restaurant } from "@mui/icons-material";
import MenuItemCard from "./MenuItemCard";
import { LUXURY } from "data/guestLuxuryTheme";

const ItemGrid = ({ items, onAddToCart, onDirectAdd, luxury }) => (
  <Grid container spacing={luxury ? 1.4 : 1}>
    {items.map((item) => (
      <Grid
        size={{ xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 }}
        key={item.id}
        sx={{ display: "flex", justifyContent: "flex-start" }}
      >
        <MenuItemCard
          item={item}
          luxury={luxury}
          categoryName={item.categoryName}
          onAddToCart={onAddToCart}
          onDirectAdd={onDirectAdd}
        />
      </Grid>
    ))}
  </Grid>
);

const MenuItemsSection = ({
  luxury = false,
  selectedCategory,
  categories,
  searchQuery,
  filteredItems,
  onAddToCart,
  onDirectAdd,
  onResetFilters,
}) => {
  const theme = useTheme();
  const groupByCategory =
    luxury && selectedCategory === "all" && !String(searchQuery || "").trim();

  const grouped = useMemo(() => {
    if (!groupByCategory) return [];
    return categories
      .map((category) => ({
        id: category.id,
        name: category.name,
        items: filteredItems.filter((item) => item.categoryId === category.id),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupByCategory, categories, filteredItems]);

  if (filteredItems.length === 0) {
    return (
      <Box sx={{ position: "relative" }}>
        <Card
          sx={{
            position: "relative",
            zIndex: 1,
            p: { xs: 4, sm: 8 },
            textAlign: "center",
            borderRadius: luxury ? "24px" : "20px",
            bgcolor: luxury ? LUXURY.card : "#ffffff",
            border: luxury ? `1px solid ${LUXURY.cardBorder}` : "none",
            boxShadow: luxury
              ? LUXURY.cardShadow
              : `0 4px 16px ${alpha("#000000", 0.06)}`,
          }}
        >
          <Restaurant
            sx={{
              fontSize: 72,
              color: luxury ? alpha(LUXURY.forest, 0.22) : alpha("#c5cfe0", 0.6),
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            fontWeight={800}
            gutterBottom
            sx={{ color: luxury ? LUXURY.ink : "#6b7567", letterSpacing: luxury ? "-0.03em" : 0 }}
          >
            No items found
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: luxury ? LUXURY.hint : "#9ca3af",
              mb: 3,
              maxWidth: 400,
              mx: "auto",
            }}
          >
            We couldn't find any menu items matching your criteria. Try
            adjusting your search or filters.
          </Typography>
          <Button
            variant="contained"
            onClick={onResetFilters}
            sx={{
              bgcolor: luxury ? LUXURY.forest : alpha(theme.palette.primary.main, 0.1),
              color: luxury ? LUXURY.cream : theme.palette.primary.main,
              borderRadius: luxury ? "999px" : "14px",
              px: 4,
              py: 1.25,
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.95rem",
              boxShadow: luxury ? "none" : `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
              "&:hover": {
                bgcolor: luxury ? LUXURY.forestDeep : alpha(theme.palette.primary.main, 0.15),
                transform: "translateY(-2px)",
              },
              transition: "all 0.25s ease-out",
            }}
          >
            Reset All Filters
          </Button>
        </Card>
      </Box>
    );
  }

  if (groupByCategory) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {grouped.map((group) => (
          <Box key={group.id}>
            <Typography
              sx={{
                fontFamily: LUXURY.displayFont,
                fontWeight: 600,
                fontSize: "1.28rem",
                color: LUXURY.ink,
                letterSpacing: 0,
                mb: 1.4,
              }}
            >
              {group.name}
            </Typography>
            <ItemGrid
              items={group.items}
              onAddToCart={onAddToCart}
              onDirectAdd={onDirectAdd}
              luxury={luxury}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      <ItemGrid
        items={filteredItems}
        onAddToCart={onAddToCart}
        onDirectAdd={onDirectAdd}
        luxury={luxury}
      />
    </Box>
  );
};

export default MenuItemsSection;
